<template>
  <div class="container">
    <h1 class="text-center">Import Vendors</h1>
    <div class="d-flex justify-content-between">
      <div class="custom-file">
        <input
          type="file"
          class="custom-file-input"
          @change="onChange"
          @click="onClick"
          accept=".xls, .xlsx"
        />
        <label class="custom-file-label">Choose file</label>
      </div>
      <button
        class="btn btn-info col-auto ml-3"
        v-if="isValid"
        @click="viewContent = !viewContent"
      >
        <i
          class="fa mr-2"
          :class="`${viewContent ? 'fa-eye-slash' : 'fa-eye'}`"
        ></i>
        {{ viewContent ? "Hide" : "Show" }} file
      </button>
    </div>
    <div
      class="alert alert-danger mt-4"
      role="alert"
      v-if="error"
      v-html="error"
    ></div>
    <div class="alert alert-warning mt-4" role="alert" v-if="warningLines">
      <b><i class="fas fa-exclamation-triangle mr-1"></i> Warning:</b><br />
      Below <b>{{ warningCount }}</b> lines will be ignored due to missing
      requirements:<br />
      <div
        v-html="warningLines"
        style="max-height: 400px; overflow: auto"
      ></div>
    </div>
    <div class="text-center" v-if="isValid">
      <button class="btn btn-success mt-4 mb-3" @click="uploadVendors">
        <i class="fa fa-lg mr-2 fa-file-upload"></i>
        Import {{ warningLines ? "anyway" : "vendors" }}
      </button>
    </div>
    <xlsx-read :file="file">
      <div
        class="file-content"
        :class="`${viewContent ? 'expanded shadow-card rounded' : 'collapsed'}`"
      >
        <div
          class="table table-sm table-striped table-dark mb-0 overflow-auto sheet-container"
        >
          <xlsx-table v-if="isValid" sheet="Vendors" />
        </div>
      </div>
      <xlsx-json class="u-display-none" sheet="Vendors" @parsed="parseSheet">
        <template #default="{ collection }">
          <div>
            {{ collection }}
          </div>
        </template>
      </xlsx-json>
    </xlsx-read>

    <h2
      class="mt-5 text-primary accordion-header"
      data-toggle="collapse"
      href="#collapseAvatars"
      role="button"
      aria-expanded="false"
      ref="collapseAvatars"
      aria-controls="collapseInstuctions"
    >
      Avatar Types <i class="ml-2 fas fa-caret-up"></i>
    </h2>
    <div class="collapse" id="collapseAvatars">
      <div class="card card-body shadow-card">
        <table class="table mb-0 rounded">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th class="text-center">Image</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(avatar, index) in vendorAvatars" :key="avatar.id">
              <td class="font-weight-bold text-primary">
                {{ index == 0 ? "-" : index }}
              </td>
              <td>{{ avatar.tooltip }}</td>
              <td class="text-center">
                <img
                  v-tooltip.top="avatar.tooltip"
                  class="avatar-image"
                  :src="avatar.src"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <hr />
    <h2
      class="mt-5 text-primary accordion-header"
      data-toggle="collapse"
      href="#collapseInstuctions"
      role="button"
      aria-expanded="true"
      aria-controls="collapseInstuctions"
    >
      Instructions <i class="ml-2 fas fa-caret-up"></i>
    </h2>
    <div class="collapse show" id="collapseInstuctions">
      <div class="card card-body shadow-card">
        <ul>
          <li class="mt-2">
            The imported excel file should have sheet called
            <span
              style="
                background-color: #e3ebf6;
                padding-left: 4px;
                padding-right: 4px;
                color: blueviolet;
                border-radius: 3px;
              "
              >Vendors</span
            >
            (<span style="text-decoration: underline">case sensitive</span>).
          </li>
          <li class="mt-3">
            The sheet should contain below headers (<span
              style="text-decoration: underline"
              >case sensitive</span
            >):
            <ul>
              <li class="mt-2">
                <b>Name</b>
                <span
                  class="bg-danger pt-1 pb-1 pl-2 pr-2 text-white required-badge"
                  >Required</span
                >
                <span
                  class="bg-dark pt-1 pb-1 pl-2 pr-2 text-white unique-badge"
                  >Unique</span
                >
              </li>
              <li class="mt-3">
                <b>Email</b>
                <span
                  class="bg-dark pt-1 pb-1 pl-2 pr-2 text-white unique-badge"
                  >Unique</span
                >
              </li>
              <li class="mt-3"><b>Website</b></li>
              <li class="mt-3"><b>Phone</b></li>
              <li class="mt-3">
                <b>Status</b>
                <br />The value can be:
                <ul>
                  <li>
                    Active
                    <span
                      class="bg-secondary pt-1 pb-1 pl-2 pr-2 text-white required-badge"
                      >Default</span
                    >
                  </li>
                  <li>Inactive</li>
                </ul>
              </li>
              <li class="mt-3"><b>Bio</b></li>
              <li class="mt-3">
                <b>Avatar ID</b>
                <br />
                Default avatar will be used if the value is empty
                <div class="d-flex align-items-center">
                  <img
                    class="avatar-image"
                    style="filter: none !important"
                    :src="vendorAvatars[0].src"
                  />
                  <div>
                    <span
                      class="bg-secondary pt-1 pb-1 pl-2 pr-2 text-white required-badge"
                      >Default</span
                    >
                    <p
                      class="btn-link cursor-pointer mb-0 ml-3 d-inline-flex"
                      @click="navigateToAvatars()"
                    >
                      See Available Avatars
                    </p>
                  </div>
                </div>
              </li>
              <li class="mt-3">
                <b>Primary Admins</b><br />
                If provided, the format
                <span style="text-decoration: underline">should be</span>:
                <span class="bg-warning pt-1 pb-1 pl-2 pr-2 required-badge"
                  >Email1 ; Email2 ; Email3</span
                >
                <br />Where the emails are seperated by semicolon. <br />The
                emails should be exist in the system. Otherwise, will be
                ignored.
                <a href="/admin/users" target="_blank">See Available Users</a>
              </li>
              <li class="mt-3">
                <b>Locations</b><br />
                If provided, the format
                <span style="text-decoration: underline">should be</span>:
                <span class="bg-warning pt-1 pb-1 pl-2 pr-2 required-badge"
                  >FirstLocation::facilityName ; SecondLocation</span
                >
                <br />Where the locations are seperated by semicolon. <br />We
                will try to detect the location for you. Otherwise, will be
                ignored.<br />Clinic/Facility/Professional name is optional and
                can be added preceded by a double colon.
              </li>
              <li class="mt-3">
                <b>Services</b><br />
                If provided, the format
                <span style="text-decoration: underline">should be</span>:
                <span class="bg-warning pt-1 pb-1 pl-2 pr-2 required-badge"
                  >Service1 ; Service2 ; Service3</span
                >
                <br />Where the services/specialties are seperated by semicolon.
                <br />The services/specialties should be exist in the system
                (case sensitive). Otherwise, will be ignored.
                <a href="/admin/services" target="_blank"
                  >See Available Services</a
                >
              </li>
              <li class="mt-3"><b>Booking Link</b></li>
            </ul>
          </li>
          <li class="mt-3">The order for sheet headers is not mandatory.</li>
          <li class="mt-3">Make sure the sheet has no empty rows.</li>
          <li class="mt-3">
            Make sure the format for all cells is
            <b class="text-danger">Text</b>
            <span
              class="bg-danger pt-1 pb-1 pl-2 pr-2 text-white required-badge"
              >Important</span
            >
            <br />
            <br />
            <img class="excel-image" src="/images/excel-format.png" />
          </li>
        </ul>
        <hr />
        <div class="d-flex justify-content-center">
          <a
            download="vendors.xlsx"
            class="btn btn-info mt-3"
            :href="`/vendors.xlsx`"
            rel="xlsx"
          >
            Download Example
          </a>
        </div>
      </div>
    </div>
    <loader v-if="processing"
      ><b class="mt-4 text-center" v-html="processingText"></b>
    </loader>
  </div>
</template>
<script>
import { XlsxRead, XlsxJson, XlsxTable } from "vue-xlsx";
import { mapActions } from "vuex";
import Swal from "sweetalert2";
import Loader from "../../components/Loader.vue";
import constants from "./constants";

export default {
  name: "VendorsImport",
  components: {
    XlsxRead,
    XlsxJson,
    XlsxTable,
    Loader,
  },
  data() {
    return {
      viewContent: false,
      addressChanged: true,
      isValid: false,
      vendors: [],
      warningLines: "",
      warningCount: 0,
      vendorAvatars: constants.AVATARS,
      error: "",
      loading: false,
      processing: false,
      processingText: "",
      file: null,
      publicPath: process.env.BASE_URL,
      errors: [],
      percent: -1,
    };
  },
  methods: {
    ...mapActions({
      importVendors: "vendors/importVendors",
    }),
    onChange(event) {
      this.file = event.target.files ? event.target.files[0] : null;
    },
    onClick(event) {
      if (event.target) {
        event.target.value = "";
        this.addressChanged = true;
      }
    },
    navigateToAvatars() {
      if (this.$refs.collapseAvatars.getAttribute("aria-expanded") != "true") {
        this.$refs.collapseAvatars.click();
      }
      this.$refs.collapseAvatars.scrollIntoView({ behavior: "smooth" });
    },
    parseSheet(data) {
      if (data.length) {
        this.isValid = true;
        this.error = "";
        this.processVendorsData(data);
      } else {
        this.isValid = false;
        if (this.file.type.indexOf("sheet") == -1) {
          this.error = "Invalid file.";
        } else {
          this.error = "Empty file.";
        }
      }
    },
    processVendorsData(data) {
      this.vendors = [];
      this.warningLines = "";
      this.warningCount = 0;
      const emails = [];
      const names = [];
      data.forEach((record, index) => {
        const vendor = {
          name: (record["Name"] || "").toString().trim(),
          email: (record["Email"] || "").toString().trim(),
          phone: (record["Phone"] || "").toString().trim(),
          site: (record["Website"] || "").toString().trim(),
          booking_link: (record["Booking link"] || "").toString().trim(),
          avatar_type: (record["Avatar ID"] || "").toString().trim(),
          bio: (record["Bio"] || "").toString().trim(),
          locations: this.getRecordLocations(record),
          services: this.getRecordServices(record),
          admins: this.getRecordAdmins(record),
          status: this.getRecordStatus(record),
        };

        if (!this.checkWarning(vendor, index, emails, names)) {
          this.vendors.push(vendor);
          emails.push(vendor.email);
          names.push(vendor.name);
        }
      });
    },
    getRecordStatus: function (record) {
      const value = (record["Status"] || "").toString().trim().toLowerCase();
      if (value == "inactive") {
        return 0;
      } else {
        return 1;
      }
    },
    getRecordServices: function (record) {
      if ((record["Services"] || "").toString().trim()) {
        const services = [];
        const mentionedServices = record["Services"]
          .toString()
          .trim()
          .split(";");
        mentionedServices.forEach((mentionedService) => {
          if (mentionedService.toLowerCase().trim()) {
            services.push(mentionedService.toLowerCase().trim());
          }
        });
        return services;
      }
      return [];
    },
    getRecordLocations: function (record) {
      const locations = [];
      const mentionedLocations = [];
      if ((record["Locations"] || "").toString().trim()) {
        mentionedLocations.push(
          ...record["Locations"].toString().trim().split(";")
        );
      }
      if ((record["Locations2"] || "").toString().trim()) {
        mentionedLocations.push(
          ...record["Locations2"].toString().trim().split(";")
        );
      }
      if ((record["Locations3"] || "").toString().trim()) {
        mentionedLocations.push(
          ...record["Locations3"].toString().trim().split(";")
        );
      }
      if ((record["Locations4"] || "").toString().trim()) {
        mentionedLocations.push(
          ...record["Locations4"].toString().trim().split(";")
        );
      }
      if ((record["Locations5"] || "").toString().trim()) {
        mentionedLocations.push(
          ...record["Locations5"].toString().trim().split(";")
        );
      }
      mentionedLocations.forEach((mentionedLocation) => {
        if (mentionedLocation.toLowerCase().trim()) {
          locations.push(mentionedLocation.toLowerCase().trim());
        }
      });
      const uniqueLocations = locations.filter((element, index) => {
        return locations.indexOf(element) === index;
      });
      return uniqueLocations;
    },
    getRecordAdmins: function (record) {
      if ((record["Primary Admins"] || "").toString().trim()) {
        const admins = [];
        const mentionedAdmins = record["Primary Admins"]
          .toString()
          .trim()
          .split(";");
        mentionedAdmins.forEach((mentionedAdmin) => {
          if (mentionedAdmin.toLowerCase().trim()) {
            admins.push(mentionedAdmin.toLowerCase().trim());
          }
        });
        return admins;
      }
      return [];
    },
    checkEmail: function (email) {
      if (!email) {
        return true;
      }
      const emailDotIndex = (email || "").lastIndexOf(".");
      if (emailDotIndex <= (email || "").indexOf("@") + 1) {
        return false;
      }
      return true;
    },
    checkWarning: function (vendor, index, emailsList, namesList) {
      if (
        !vendor.name ||
        !this.checkEmail(vendor.email) ||
        emailsList.includes(vendor.email) ||
        namesList.includes(vendor.name)
      ) {
        let exactError = "";
        if (!vendor.name) {
          exactError = "Name is missing.";
        } else if (!this.checkEmail(vendor.email)) {
          exactError = "Invalid email is used.";
        } else if (namesList.includes(vendor.name)) {
          exactError = "Same name is used in another record.";
        } else if (emailsList.includes(vendor.email)) {
          exactError = "Same email is used in another record.";
        }
        this.warningCount++;
        this.warningLines = `${this.warningLines}<b>${
          index + 2
        }</b> - ${exactError}<br>`;
        return true;
      } else if (vendor.admins.length) {
        vendor.admins.forEach((admin) => {
          if (!this.checkEmail(admin)) {
            this.warningCount++;
            this.warningLines = `${this.warningLines}<b>${
              index + 2
            }</b> - Primary admin (${admin}) is not a valid email address. Will be ignored<br>`;
          }
        });
      } else if (vendor.locations.length) {
        let hasError = false;
        vendor.locations.forEach((location) => {
          if (!location.toLowerCase().split("::")[0].trim()) {
            hasError = true;
            this.warningCount++;
            this.warningLines = `${this.warningLines}<b>${
              index + 2
            }</b> - Location (${location}) is not a valid location. Will be ignored<br>`;
          }
        });
        if (hasError) {
          vendor.locations = vendor.locations.filter((item) =>
            item.toLowerCase().split("::")[0].trim()
          );
        }
      }
      return false;
    },
    setProgressMessage: function (message) {
      const percentMessage = `<h1 style="color: mediumspringgreen">${this.percent} %</h1>`;
      this.processingText = `${
        this.percent > -1 ? percentMessage : ""
      }${message}`;
    },
    sendVendorsToServer: function () {
      this.percent = 0;
      this.setProgressMessage(
        "Preparing vendors to import... (uploading vendors)<br>This may take few moments. Please don't refresh the page."
      );
      let vendorsToSend = new Array(...this.vendors);
      var vendorsSeparated = [];
      while (vendorsToSend.length) {
        vendorsSeparated.push(vendorsToSend.splice(0, 30));
      }

      this.errors = [];
      const vendorsPromises = [];
      vendorsSeparated.forEach((chuck, index) => {
        if (index == 0) {
          vendorsPromises.push(this.importVendorsChunck(chuck));
        } else {
          vendorsPromises[index] = vendorsPromises[index - 1].then(() => {
            this.percent = Math.ceil(
              ((index * 30) / this.vendors.length) * 100
            );
            this.setProgressMessage(
              "Preparing vendors to import... (uploading vendors)<br>This may take few moments. Please don't refresh the page."
            );
            return this.importVendorsChunck(chuck);
          });
        }
      });
      vendorsPromises[vendorsSeparated.length - 1].then(() => {
        this.fireError();
        if (this.errors.length) {
          this.error = `<b>Issues while importing the vendors:</b><br><ul><li>${this.errors.join(
            "</li><li>"
          )}</li></ul>`;
        } else {
          Swal.fire({
            title: "Vendors Imported",
            text: "Vendors Imported Successfully",
            type: "success",
          });
        }
      });
    },
    importVendorsChunck: function (data) {
      return new Promise((resolve) => {
        this.importVendors({ vendors: data }).then((res) => {
          if (res.errors && res.errors.length) {
            this.errors.push(...res.errors);
          } else if (res.error && res.error.message) {
            this.errors.push(res.error.message);
          }
          resolve();
        });
      });
    },
    uploadVendors: function () {
      if (!this.vendors.length) {
        this.fireError("No valid vendors to import!");
      } else {
        this.processing = true;
        this.setProgressMessage(
          "Preparing vendors to import... (getting vendors addresses)<br>This may take few moments. Please don't refresh the page."
        );
        const addressesToDetect = [];
        const addressPromises = [];
        let addressesRequestsLength = 0;
        this.vendors.forEach((vendor, index) => {
          if (vendor.locations.length) {
            vendor.locations.forEach((loc) => {
              addressesToDetect.push({
                location: loc,
                vendorIndex: index,
              });
            });
          }
        });

        if (addressesToDetect.length && this.addressChanged) {
          addressesToDetect.forEach((item, index) => {
            addressesRequestsLength++;
            setTimeout(() => {
              addressPromises.push(this.getGoogleAddress(item));
            }, 70 * index);
          });
          setTimeout(() => {
            Promise.all(addressPromises).then(() => {
              this.setProgressMessage(
                "Preparing vendors to import...<br>This may take few moments. Please don't refresh the page."
              );
              this.addressChanged = false;
              this.sendVendorsToServer();
            });
          }, 70 * addressesRequestsLength);
        } else {
          this.setProgressMessage(
            "Preparing vendors to import...<br>This may take few moments. Please don't refresh the page."
          );
          this.addressChanged = false;
          this.sendVendorsToServer();
        }
      }
    },
    getGoogleAddress: function (info) {
      let vm = this;
      return new Promise(function (resolve) {
        if (!info.location.split("::")[0]) {
          return resolve();
        }
        vm.$http
          .get(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
              info.location.split("::")[0]
            )}&key=${
              "AIzaSyCXAVpVS4FLtDCukiDoOAnG-Mb4vnI91Zk" ||
              process.env.VUE_APP_GOOGLE
            }`
          )
          .then((res) => {
            if (res.status == 200 && res.data.status == "OK") {
              vm.selectAddress(res.data.results[0], info);
              resolve();
            } else {
              const array_index = vm.vendors[
                info.vendorIndex
              ].locations.indexOf(info.location);
              vm.vendors[info.vendorIndex].locations.splice(array_index, 1);
              resolve();
            }
          })
          .catch(() => {
            resolve();
          });
      });
    },
    selectAddress: function (location, info) {
      var ShouldBeComponent = {
        number: ["street_number"],
        unit: ["subpremise"],
        zip: ["postal_code"],
        street: ["street_address", "route"],
        state: [
          "administrative_area_level_1",
          "administrative_area_level_2",
          "administrative_area_level_3",
          "administrative_area_level_4",
          "administrative_area_level_5",
        ],
        city: [
          "locality",
          "sublocality",
          "sublocality_level_1",
          "sublocality_level_2",
          "sublocality_level_3",
          "sublocality_level_4",
        ],
        country: ["country"],
      };

      let address = {
        number: "",
        unit: "",
        zip: "",
        street: "",
        state: "",
        city: "",
        country: "",
        lat: "",
        lng: "",
      };

      location.address_components.forEach((component) => {
        for (let shouldBe in ShouldBeComponent) {
          if (ShouldBeComponent[shouldBe].indexOf(component.types[0]) !== -1) {
            if (
              shouldBe === "country" ||
              shouldBe === "state" ||
              shouldBe === "street"
            ) {
              address[shouldBe] = component.short_name;
            } else {
              address[shouldBe] = component.long_name;
            }
          }
        }
      });

      address["lat"] = location.geometry.location.lat;
      address["lng"] = location.geometry.location.lng;

      const requiredFields = ["city", "state", "country", "zip", "lat", "lng"];
      let hasError = false;
      requiredFields.forEach((item) => {
        if (!address[item]) {
          hasError = true;
        }
      });
      if (hasError) {
        const array_index = this.vendors[info.vendorIndex].locations.indexOf(
          info.location
        );
        this.vendors[info.vendorIndex].locations.splice(array_index, 1);
      } else {
        const array_index = this.vendors[info.vendorIndex].locations.indexOf(
          info.location
        );
        this.vendors[info.vendorIndex].locations[array_index] = {
          ...address,
          locationName: (info.location || "").split("::")[0],
          clinic_name: (info.location.split("::")[1] || "").trim(),
        };
      }
    },
    fireError: function (error) {
      this.error = error;
      this.warningLines = "";
      this.warningCount = 0;
      this.processing = false;
      this.processingText = "";
      this.percent = -1;
    },
  },
};
</script>

<style lang="scss">
.sheet-container {
  > div {
    display: grid;
  }
  a {
    color: #add8e6;
  }
}
</style>
<style scoped lang="scss">
.avatar-image {
  width: 70px;
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: scale(1.15);
  }
}
.accordion-header {
  i {
    transition: all 0.3s ease-in-out;
  }
  &[aria-expanded="false"] {
    i {
      transform: rotate(180deg);
    }
  }
}
.file-content {
  overflow: hidden;
  transition: all 0.5s ease-in-out;
  &.collapsed {
    max-height: 0;
  }
  &.expanded {
    max-height: 9999px;
    overflow-y: auto;
  }
}
.alert-danger {
  color: #721c24;
  background-color: #f8d7da;
  border-color: #f5c6cb;
  max-height: 415px;
  overflow: auto;
}
.alert-warning {
  color: #856404;
  background-color: #fff3cd;
  border-color: #ffeeba;
}
.required-badge {
  font-size: 10px;
  margin-left: 8px;
  border-radius: 6px;
  font-weight: bold;
}
.unique-badge {
  font-size: 10px;
  margin-left: 8px;
  border-radius: 6px;
  font-weight: bold;
}

.excel-image {
  max-width: 90%;
}

.loader {
  top: 0;
  left: 0;
  right: 0;
  border: 0;
  z-index: 99999;
  background: rgba(0, 0, 0, 0.7);
  color: whitesmoke;
  flex-direction: column;
}
.u-display-none {
  display: none !important;
}
</style>
